import axios, { AxiosError, AxiosInstance } from 'axios';
import history from 'routes/history';
import { buildLocStoreKeys } from 'store/types';
import { setAuthTokensFromResponse, getRefreshToken, getAccessToken, resetStorage, getUserInfo } from 'utils/auth';
import apiEventEmitter from 'core/EventEmitter';
import i18n from 'core/i18n';
import { getSubdomain } from 'utils/helpers/routing';

declare module 'axios' {
  interface AxiosRequestConfig {
    suppressGlobalServerError?: boolean;
  }
}

/**
 * Singleton class Api
 *
 * Solution inspired by examples here:
 * https://gist.github.com/mkjiau/650013a99c341c9f23ca00ccb213db1c
 */

interface IRequestPromise {
  resolve: () => void;
  reject: (err: AxiosError) => void;
}

class Api {
  private static instance: AxiosInstance;

  private static isRefreshing = false;

  private static refreshQueue: IRequestPromise[] = [];

  private static retries = 1;

  private constructor() {}

  static getInstance(): AxiosInstance {
    const LocStoreKeys = buildLocStoreKeys();

    if (!this.instance) {
      this.instance = axios.create({
        baseURL: this.getBaseUrl(),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      this.instance.interceptors.request.use(config => {
        return new Promise((resolve, reject) => {
          /**
           * while updating tokens with refresh mechanism
           * push all incoming requests into `refreshQueue`
           * so that they don't hang after refresh is complete
           */
          if (this.isRefreshing && config.url !== 'auth/refresh') {
            this.refreshQueue.push({
              resolve: () => {
                resolve(config);
              },
              reject: err => {
                reject(err);
              },
            });
          }
          /**
           * We need to override the original request Authorization header
           * of the requests that has been in the queue in case
           * this header is no longer valid and has been updated
           * after refresh token mechanism
           */
          resolve({
            ...config,
            headers: {
              ...config.headers,
              Authorization: this.getAuthHeader(),
            },
          });
        });
      });

      this.instance.interceptors.response.use(
        // Success responses are handled, network status managed by useVersionCheck
        response => {
          // Notify useVersionCheck of successful API calls to clear error messages
          apiEventEmitter.emit('api-success');
          return response;
        },
        // However, do something with response error:
        async error => {
          const originalRequest = error.config;
          const shouldSuppressGlobalServerError = Boolean(originalRequest?.suppressGlobalServerError);
          const errorMessage = String(error?.message || '').toLowerCase();
          const isTooLargeUploadError = error?.response?.status === 413 || errorMessage.includes('413');

          if (!error.response) {
            if (shouldSuppressGlobalServerError) {
              return Promise.reject(error);
            }
            if (isTooLargeUploadError) {
              return Promise.reject(error);
            }
            apiEventEmitter.emit('api-connection-error', error);
            return Promise.reject(error);
          }

          if (error.response) {
            const isUnauthorised = error.response?.status === 401;

            const is2FARequired = error.response?.data?.two_factor_required;

            const isAccessTokenSet = getAccessToken();

            /**
             * Redirect to 2FA activation page
             * if user has NOT set yet his 2FA
             * and update tokens if this is coming from 2FA reset
             */
            if (isUnauthorised && is2FARequired && isAccessTokenSet) {
              // Update tokens and Auth header
              await setAuthTokensFromResponse(error.response);

              // Redirect to 2FA activate page
              history.replace('/profile/2fa-activate');

              // Update storage flag state
              localStorage.setItem(LocStoreKeys.IS_2FA_COMPLETE, 'false');

              return Promise.reject(error);
            }

            /**
             * If the error is due to authentication and
             * is NOT coming from expired refresh token
             * (exclude refresh token endpoint to prevent infinite loops):
             */
            if (originalRequest.url !== 'auth/refresh' && isUnauthorised && isAccessTokenSet) {
              /**
               * If access token has expired, push all pending requests in `this.refreshQueue`,
               * refresh access token and retry queued requests
               * but if error occurs don't exceed number of retries
               */

              originalRequest._retry = typeof originalRequest._retry === 'undefined' ? 0 : originalRequest._retry + 1;

              if (originalRequest._retry === this.retries) {
                return Promise.reject(error);
              }

              if (!this.isRefreshing) {
                this.isRefreshing = true;

                // Refresh the token and retry all requests from queue
                this.instance
                  .post('auth/refresh', {
                    refresh_token: getRefreshToken(),
                  })
                  .then(async res => {
                    // Update tokens and Auth header
                    this.instance.defaults.headers.common.Authorization = `Bearer ${res.data.access_token}`;
                    await setAuthTokensFromResponse(res);
                    originalRequest.headers.Authorization = this.instance.defaults.headers.common.Authorization;

                    this.refreshQueue.forEach(v => v.resolve());
                    this.refreshQueue = [];
                  })
                  .catch(err => {
                    // Logging out if all tokens had expired
                    // Reset Auth tokens to prevent infinite loops
                    const userInfo = getUserInfo();

                    resetStorage();
                    history.push('/logout', {
                      from: window.location.pathname,
                      email: userInfo.email,
                    });
                    this.refreshQueue.forEach(v => v.reject(err));
                    this.refreshQueue = [];
                  })
                  .finally(() => {
                    this.isRefreshing = false;
                  });
              }

              return new Promise((resolve, reject) => {
                this.refreshQueue.push({
                  resolve: () => {
                    resolve(this.instance(originalRequest));
                  },
                  reject: err => {
                    reject(err);
                  },
                });
              });
            }
            /**
             * If error status is not 401
             *
             * OR
             *
             * error is coming from expired refresh token,
             * return error response:
             */
            // Server responses indicate connectivity is working, network status handled by useVersionCheck
            return Promise.reject(error.response ? error.response : error);
          }
        }
      );

      // Add event listener for storage events to sync auth tokens across tabs
      window.addEventListener('storage', event => {
        if (event.key === LocStoreKeys.ACCESS_TOKEN) {
          this.setAuthToken(event.newValue);
        }
      });
    }

    return this.instance;
  }

  static getAuthHeader(): string {
    return Api.getInstance().defaults.headers.common.Authorization;
  }

  static getBaseUrl(): string {
    const subdomain = getSubdomain();
    const baseUrl = process.env.REACT_APP_API_URL!.replace('subdomain', subdomain);

    return baseUrl;
  }

  static setAuthToken(token: string | null | undefined): void {
    if (token) {
      Api.getInstance().defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      Api.getInstance().defaults.headers.common.Authorization = '';
    }
  }

  static setClientApiUrl(clientApi: string): void {
    Api.getInstance().defaults.baseURL = clientApi;
  }

  static resetApiUrl(): void {
    Api.getInstance().defaults.baseURL = Api.getBaseUrl();
  }
}

export const { setAuthToken, setClientApiUrl, resetApiUrl } = Api;

export default Api.getInstance();
